import React from 'react';
import { Box, Typography, Grid } from '@mui/material';

/**
 * DashboardSection - A row-based section container for dashboard widgets
 *
 * Creates a labeled section with widgets arranged in a row.
 * All widgets in the section share the same height for visual consistency.
 *
 * @param {string} title - Section header text
 * @param {React.ReactNode} children - Widget components to render in the row
 * @param {number} columns - Number of columns (2, 3, or 4). Default: 3
 */
const DashboardSection = ({ title, children, columns = 3 }) => {
  // Calculate grid column width based on number of columns
  const getColumnWidth = () => {
    switch (columns) {
      case 2:
        return { xs: 12, md: 6 };
      case 4:
        return { xs: 12, sm: 6, md: 3 };
      case 3:
      default:
        return { xs: 12, sm: 6, md: 4 };
    }
  };

  const columnWidth = getColumnWidth();

  // Convert children to array for mapping
  const childArray = React.Children.toArray(children);

  return (
    <Box sx={{ mb: 4 }}>
      {/* Section Header */}
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 600,
          color: 'text.secondary',
          mb: 2,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          fontSize: '0.75rem',
        }}
      >
        {title}
      </Typography>

      {/* Widgets Grid - alignItems stretch ensures equal heights */}
      <Grid container spacing={3} sx={{ alignItems: 'stretch' }}>
        {childArray.map((child, index) => (
          <Grid
            item
            key={index}
            {...columnWidth}
            sx={{
              display: 'flex',
              '& > *': {
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
              },
            }}
          >
            {child}
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default DashboardSection;
